import { Component, Prop, h, Host, Element } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

const COMPONENT_NAME = 'sn-disclaimer';

const DEFAULT_DESCRIPTION =
  '<strong>If you believe that this page should be taken down, please follow our <a class="sn-disclaimer__link" href="https://legal.signnow.com/content-takedown-policy">DMCA take-down process</a>.</strong>';

const ICON_SRC =
  'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxOSIgaGVpZ2h0PSIyMSIgZmlsbD0ibm9uZSI+PHBhdGggc3Ryb2tlPSIjMDE4MkU4IiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIHN0cm9rZS13aWR0aD0iMS41IiBkPSJNMTMuNTAyIDIwLjAwNGE0LjUwMiA0LjUwMiAwIDEgMSAwLTkuMDA0IDQuNTAyIDQuNTAyIDAgMCAxIDAgOS4wMDRNMTQuNzcyIDE0LjIzM2wtMi41NCAyLjUzOU0xNC43NzIgMTYuNzcybC0yLjU0LTIuNTQiLz48cGF0aCBzdHJva2U9IiMwMTgyRTgiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIgc3Ryb2tlLXdpZHRoPSIxLjUiIGQ9Ik0xNS4wMDIgOFY1LjgyNWMwLS41My0uMjExLTEuMDQtLjU4Ni0xLjQxNGwtMi44My0yLjgzYTIgMiAwIDAgMC0xLjQxNC0uNTg2SDIuOTk3YTIgMiAwIDAgMC0yIDIuMDAxdjE0LjAwNmEyIDIgMCAwIDAgMiAyaDQuMDAyIi8+PHBhdGggc3Ryb2tlPSIjMDE4MkU4IiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIHN0cm9rZS13aWR0aD0iMS41IiBkPSJNMTUuMDAyIDUuOTk4SDExYTEgMSAwIDAgMS0xLTFWLjk5NiIvPjwvc3ZnPg==';

/**
 * DMCA take-down notice disclaimer banner.
 * Full-width dashed-border box with a document icon and a text containing an inline link.
 *
 * The sentence lives in the `description` prop and HTML is allowed there, so the text and the
 * take-down link are both editable in the visual editor. It is a prop rather than slot content
 * because the story-schema generator flattens slot content to a single plain string — an inline
 * link authored inside a slot is dropped from the published block's default data. The `text` slot
 * stays available for pages that want to compose the paragraph out of elements, and the legacy
 * `values` object still renders when neither is set.
 *
 * @product signnow
 * @version 2.1.0
 * @category SignNow
 * @contentConfig DMCA take-down notice disclaimer
 * @slot text - <me-text tag="p"> - Optional element-level replacement for the whole paragraph
 * @example
 *   <sn-disclaimer-type-1
 *     description='<strong>If you believe that this page should be taken down, please follow our <a class="sn-disclaimer__link" href="https://legal.signnow.com/content-takedown-policy">DMCA take-down process</a>.</strong>'
 *   ></sn-disclaimer-type-1>
 */
@Component({
  tag: 'sn-disclaimer-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SnDisclaimer {
  @Element() host!: HTMLElement;

  /**
   * Color mode for theming
   */
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Disclaimer sentence. HTML is allowed, which is where the inline take-down link lives.
   * @content
   * @dynamic
   * @default "<strong>If you believe that this page should be taken down, please follow our <a class=\"sn-disclaimer__link\" href=\"https://legal.signnow.com/content-takedown-policy\">DMCA take-down process</a>.</strong>"
   */
  @Prop({ attribute: 'description' }) description?: string;

  /**
   * Legacy combined payload, kept as the fallback for pages authored against v2. `description`
   * wins when set — prefer it, since the editor renders an object prop as a single raw-JSON
   * field where anything but valid JSON is silently dropped.
   * @dynamic
   * @type {object}
   * @property {string} text - Main disclaimer text displayed before the link
   * @property {string} linkText - Anchor text for the inline link
   * @property {string} linkHref - URL for the inline link
   * @required text
   * @required linkText
   * @required linkHref
   */
  @Prop({ mutable: true }) values?: {
    text?: string;
    linkText?: string;
    linkHref?: string;
  } | string;

  /**
   * With `shadow: false` Stencil does not suppress a slot's fallback content when the slot is
   * filled — both would render — so the paragraph below is gated on this check instead.
   */
  private hasSlottedText = false;

  componentWillLoad() {
    this.values = parseJsonProp(this.values);
    this.hasSlottedText = !!this.host.querySelector('[slot="text"]');
  }

  render() {
    const v = typeof this.values === 'object' && this.values !== null
      ? this.values as Record<string, string>
      : {};
    // The legacy object only wins while `description` is unset; otherwise the prop (or its
    // default) renders as HTML so the inline link survives.
    const useLegacyValues = !this.description && (v.text || v.linkText || v.linkHref);

    return (
      <Host data-color-mode={this.colorMode}>
        <section class={COMPONENT_NAME}>
          <div class={`${COMPONENT_NAME}__container me-container`}>
            <div class={`${COMPONENT_NAME}__box`}>
              <img class={`${COMPONENT_NAME}__icon`} src={ICON_SRC} alt="" aria-hidden="true" width="19" height="21" />
              <slot name="text"></slot>
              {!this.hasSlottedText &&
                (useLegacyValues ? (
                  <p class={`${COMPONENT_NAME}__text`}>
                    <strong>
                      {v.text}{' '}
                      <a class={`${COMPONENT_NAME}__link`} href={v.linkHref ?? 'https://legal.signnow.com/content-takedown-policy'}>
                        {v.linkText ?? 'here'}
                      </a>
                      .
                    </strong>
                  </p>
                ) : (
                  <p class={`${COMPONENT_NAME}__text`} innerHTML={this.description ?? DEFAULT_DESCRIPTION}></p>
                ))}
            </div>
          </div>
        </section>
      </Host>
    );
  }
}
